import { SafeError } from '@benzinga/safe-await';
import { UUID } from '@benzinga/session';
import { WatchlistId } from '@benzinga/watchlist-manager';
import { AutocompleteItem } from '@benzinga/autocomplete-manager';
import { DelayedQuote } from '@benzinga/quotes-manager';
import { OptionsActivity } from '@benzinga/calendar-option-activity-manager';
import { EventsAction } from './generateManagerParams';
import { SECEventEntity } from '@benzinga/calendar-sec-manager';
import type { Dividend, DividendsQueryParams } from '@benzinga/calendar-dividends-manager';

export type { Dividend };

export type CalendarFeedId = string;

export enum AnalystRatingType {
  analyst = 'analyst',
  firm = 'firm',
}

export interface AnalystRatingsParameters {
  names: string[];
  ratingType: AnalystRatingType;
}

// calendar data

export type CalendarType =
  | 'conference'
  | 'dividends'
  | 'earnings'
  | 'economics'
  | 'fda'
  | 'guidance'
  | 'ipos'
  | 'ma'
  | 'offerings'
  | 'optionsActivity'
  | 'ratings'
  | 'retail'
  | 'sec'
  | 'splits'
  | 'squawk';

export enum CalendarTypeV6 {
  conference = 'conference',
  dividends = 'dividends',
  earnings = 'earnings',
  economics = 'economics',
  fda = 'fda',
  guidance = 'guidance',
  ipos = 'ipos',
  ma = 'ma',
  offerings = 'offerings',
  optionsActivity = 'optionsActivity',
  ratings = 'ratings',
  retail = 'retail',
  sec = 'sec',
  splits = 'splits',
  squawk = 'squawk',
}

export interface Drug {
  id: string;
  indication_symptom: string[];
  name: string;
}

export interface Company {
  cik: string;
  id: string;
  name: string;
  securities: Security[];
}

export interface Security {
  exchange: string;
  symbol: string;
  logo?: string;
}

interface EventEntity {
  id: string;
  name: string;
  ticker: string;
  type: CalendarType;
  updated: number;
}

export interface FDA extends EventEntity {
  commentary: string;
  companies: Company[];
  date: string;
  drug: Drug;
  event_type: string;
  nic_number: string;
  outcome: string;
  outcome_brief: string;
  source_link: string;
  source_type: string;
  status: string;
  target_date: string;
  time: string;
  quote?: DelayedQuote;
}

export type RatingsActionCompany =
  | 'Downgrades'
  | 'Maintains'
  | 'Reinstates'
  | 'Reiterates'
  | 'Upgrades'
  | 'Assumes'
  | 'Initiates Coverage On'
  | 'Terminates Coverage On'
  | 'Removes'
  | 'Suspends'
  | 'Firm Dissolved';

export interface Ratings extends EventEntity {
  action_company?: RatingsActionCompany;
  action_pt: string;
  analyst: string;
  analyst_name: string;
  analyst_id: string;
  date: string;
  pt_prior: string;
  pt_current: string;
  rating_current: string;
  rating_prior: string;
  ratings_accuracy: AnalystRatings;
  time: string;
  type: 'ratings';
  quote?: DelayedQuote;
  url: string;
  url_calendar: string;
  url_news: string;
  adjusted_pt_prior: string;
  adjusted_pt_current: string;
  pt_pct_change: string;
}

export interface AnalystRatings {
  smart_score: string;
  overall_success_rate: string;
  total_ratings: string;
}

export interface Conference extends EventEntity {
  access_code: string;
  date: string;
  exchange: string;
  importance: string;
  international_num: string;
  phone_num: string;
  reservation_num: string;
  start_time: string;
  time: string;
  type: 'conference';
  updated: number;
  webcast_url: string;
}

type Id = string;

export interface Retail extends EventEntity {
  date: string;
  period: string;
  period_year: string;
  sss: string;
  logo?: string;
  type: 'retail';
}

export interface CalendarsRows {
  ['conference']?: Map<Id, WeakRef<Conference>>;
  ['dividends']?: Map<Id, WeakRef<Dividend>>;
  ['earnings']?: Map<Id, WeakRef<Earnings>>;
  ['economics']?: Map<Id, WeakRef<Economics>>;
  ['fda']?: Map<Id, WeakRef<FDA>>;
  ['guidance']?: Map<Id, WeakRef<Guidance>>;
  ['ipos']?: Map<Id, WeakRef<IPOs>>;
  ['ma']?: Map<Id, WeakRef<MergersAndAcquisitions>>;
  ['offerings']?: Map<Id, WeakRef<Offerings>>;
  ['optionsActivity']?: Map<Id, WeakRef<UnusualOptionsActivity>>;
  ['ratings']?: Map<Id, WeakRef<Ratings>>;
  ['retail']?: Map<Id, WeakRef<Retail>>;
  ['sec']?: Map<Id, WeakRef<SEC>>;
  ['splits']?: Map<Id, WeakRef<Splits>>;
  ['squawk']?: Map<Id, WeakRef<Squawk>>;
}

export type CalendarsRowsMap = string[];

export interface CalendarsRowsKeys {
  [CalendarType: string]: Map<Id, CalendarsRowsMap>;
}

export interface Earnings extends EventEntity {
  eps_type: string;
  eps_surprise: string;
  eps_surprise_percent: string;
  revenue_type: string;
  revenue_surprise: string;
  revenue_surprise_percent: string;
  date: string;
  date_confirmed: number;
  eps: string;
  eps_est: string;
  eps_prior: string;
  exchange: string; // TODO: 'NYSE', ... ?
  id: string;
  importance: number;
  period: 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'FY';
  period_year: string;
  revenue: string;
  revenue_prior: string;
  revenue_est: string;
  time: string;
  type: 'earnings';
  updated: number;
}

export interface Economics extends EventEntity {
  actual: string;
  actual_t: string;
  consensus: string;
  consensus_t: string;
  country: string;
  date: string;
  description: string;
  event_name: string;
  event_category: string;
  event_period: string;
  importance: number;
  period_year: string;
  prior: string;
  prior_t: string;
  time: string;
  type: 'economics';
  updated: number;
}

// TODO needs attributes list
export interface Guidance extends EventEntity {
  date: string;
  time: string;
  type: 'guidance';
  eps_guidance_min: string;
  eps_guidance_max: string;
  eps_guidance_est: string;
  eps_guidance_prior_max: string;
  eps_guidance_prior_min: string;
  eps_type: string;
  revenue_guidance_max: string;
  revenue_guidance_min: string;
  revenue_guidance_prior_max: string;
  revenue_guidance_prior_min: string;
  revenue_type: string;
  ticker: string;
  period: string;
  period_year: string;
}

export interface RatingsAnalystsParams {
  analyst?: string;
  page?: string;
  fields?: string;
  pageSize?: number;
  updated?: number;
}

export interface CompanyEventsQueryParams {
  date_from?: string;
  date_to?: string;
  importance?: number;
  page?: string | number;
  pageSize?: number;
  tickers?: string;
  updated?: number;
}

export interface IPOs extends EventEntity {
  date: string;
  deal_status: string;
  exchange: string;
  insider_lockup_date: string;
  insider_lockup_days: number;
  lead_underwriters: UnderWriters;
  offering_shares: string;
  offering_value: string;
  other_underwriters: UnderWriters;
  price_max: string;
  price_min: string;
  pricing_date: string;
  shares_from_selling_holders: string;
  shares_outstanding: string;
  time: string;
  type: 'ipos';
  underwriter_quiet_expiration_date: string;
  underwriter_quiet_expiration_days: string;
  ipo_type?: string;
  updated: number;
}

export interface MergersAndAcquisitions extends EventEntity {
  acquirer_exchange: string;
  acquirer_name: string;
  acquirer_ticker: string;
  currency: string;
  date: string;
  date_completed: string;
  date_expected: string;
  deal_payment_type: string;
  deal_size: string;
  deal_status: string;
  deal_terms_extra?: string;
  deal_type: string;
  importance?: 0 | 1 | 2 | 3 | 4 | 5;
  notes?: string;
  target_exchange: string;
  target_name: string;
  target_ticker: string;
  type: 'ma';
  updated: number;
  message?: string;
}

export interface UnusualOptionsActivity extends EventEntity {
  date: string;
  time: string;
  ticker: string;
  put_call: string;
  strike_price: string;
  date_expiration: string;
  size: string;
  volume: string;
  open_interest: string;
  underlying_type: string;
  sentiment: string;
  cost_basis: string;
  type: 'optionsActivity';
  updated: number;
}

export interface SEC extends SECEventEntity {
  date: string;
  type: 'sec';
}
export interface Offerings extends EventEntity {
  date: string;
  type: 'offerings';
}

export interface Splits extends EventEntity {
  date_announced: string;
  date_distribution: string;
  date_ex: string;
  date_recorded: string;
  exchange: string;
  importance: string;
  optionable: string;
  ratio: string;
  type: 'splits';
  updated: number;
}

export interface Squawk extends EventEntity {
  type: 'squawk';
  text: string;
}

interface UnderWriters {
  id: string;
}

export interface CalendarTypeEvents {
  conference?: Conference[];
  dividends?: Dividend[];
  earnings?: Earnings[];
  economics?: Economics[];
  fda?: FDA[];
  guidance?: Guidance[];
  ipos?: IPOs[];
  ma?: MergersAndAcquisitions[];
  offerings?: Offerings[];
  optionsActivity?: OptionsActivity[];
  ratings?: Ratings[];
  retail?: Retail[];
  sec?: SEC[];
  splits?: Splits[];
  squawk?: Squawk[];
}

export type CalendarEvents =
  | Conference
  | Dividend
  | Earnings
  | Economics
  | Guidance
  | FDA
  | IPOs
  | MergersAndAcquisitions
  | Offerings
  | Ratings
  | Retail
  | UnusualOptionsActivity
  | SEC
  | Splits
  | Squawk;
export interface CalendarDataParameters {
  calendarType: CalendarType;
  dateEnd?: DefaultCalendarDate;
  dateStart?: DefaultCalendarDate;
  filters: AutocompleteItem[];
  updated?: number;
  rangeType?: string;
  watchlistIds: WatchlistId[];
}

export interface CalendarDataParametersV6 {
  calendarType: CalendarTypeV6;
  dateEnd?: DefaultCalendarDate;
  dateStart?: DefaultCalendarDate;
  filters: AutocompleteItem[];
  updated?: number;
  watchlistIds: WatchlistId[];
}
export type RatingsAction =
  | 'Downgrades'
  | 'Maintains'
  | 'Reinstates'
  | 'Reiterates'
  | 'Upgrades'
  | 'Assumes'
  | 'Initiates Coverage On'
  | 'Terminates Coverage On'
  | 'Removes'
  | 'Suspends'
  | 'Firm Dissolved';

/** Type of IPO offering */
export type IPOType = 'IPO' | 'SPAC' | 'Direct Listing';

/** Event importance level (0-5) */
export type Importance = 0 | 1 | 2 | 3 | 4 | 5;

/**
 * Sort order for earnings dates
 * - asc: Oldest to newest
 * - desc: Newest to oldest
 */
export type EarningsDateSort = 'asc' | 'desc';

/**
 * Date field to search FDA events on
 * - announced: Search by announcement date
 * - target: Search by target/PDUFA date
 */
export type FdaDateSort = 'announced' | 'target';

/**
 * Date field to sort M&A events on
 * - expected: Sort by expected completion date
 * - completed: Sort by actual completion date
 * - announced: Sort by announcement date
 */
export type MaDateSort = 'expected' | 'completed' | 'announced';

/**
 * Date field to search splits on
 * - announced: Search by announcement date
 * - ex: Search by ex-date
 */
export type SplitsDateSearchField = 'announced' | 'ex';

/** Union of all calendar-specific date sort fields */
export type DateSortField =
  | 'announced'
  | 'ex'
  | 'payable'
  | 'record'
  | EarningsDateSort
  | FdaDateSort
  | MaDateSort
  | 'date:asc'
  | 'date:desc';

/**
 * Base query parameters shared by all calendar types
 */
export interface BaseCalendarParams {
  /** Specific date to filter on (YYYY-MM-DD) */
  date?: string;
  /** Start date for date range filter */
  dateFrom?: Date | string;
  /** End date for date range filter */
  dateTo?: Date | string;
  /** Comma-separated list of fields to return */
  fields?: string;
  /** Filter by importance level (0-5) */
  importance?: Importance;
  /** Number of results per page */
  pageSize?: number;
  /** Filter by ticker symbols */
  symbols?: string[] | string;
  /** Filter by last updated timestamp (Unix epoch) */
  updated?: number;
}

/**
 * Query parameters for Earnings Calendar
 * @extends BaseCalendarParams
 */
export interface EarningsCalendarParams extends BaseCalendarParams {
  /** Sort order for earnings dates (asc/desc) */
  dateSort?: EarningsDateSort;
}

/**
 * Query parameters for Analyst Ratings Calendar
 * @extends BaseCalendarParams
 */
export interface RatingsCalendarParams extends BaseCalendarParams {
  /** Filter by rating action type */
  action?: RatingsAction;
  /** Filter by analyst name */
  analyst?: string;
  /** Filter by analyst ID */
  analystId?: string;
  /** Filter by firm name */
  firm?: string;
  /** Filter by firm ID */
  firmId?: string;
}

/**
 * Query parameters for FDA Calendar
 * @extends BaseCalendarParams
 */
export interface FdaCalendarParams extends BaseCalendarParams {
  /** Date field to search on (announced or target) */
  dateSearchField?: FdaDateSort;
  /** When true, only return events with exact date matches */
  dateSortStrict?: boolean;
  /** Filter by security symbols (FDA uses 'securities' not 'tickers') */
  securities?: string;
}

/**
 * Query parameters for IPO Calendar
 * @extends BaseCalendarParams
 */
export interface IposCalendarParams extends BaseCalendarParams {
  /** Filter by IPO type (IPO, SPAC, Direct Listing) */
  ipoType?: IPOType;
}

/**
 * Query parameters for M&A Calendar
 * @extends BaseCalendarParams
 */
export interface MaCalendarParams extends BaseCalendarParams {
  /** Date field to sort M&A events on */
  dateSort?: MaDateSort;
}

/**
 * Query parameters for Stock Splits Calendar
 * @extends BaseCalendarParams
 */
export interface SplitsCalendarParams extends BaseCalendarParams {
  /** Date field to search splits on */
  dateSearchField?: SplitsDateSearchField;
}

/**
 * Query parameters for Guidance Calendar
 * @extends BaseCalendarParams
 */
export interface GuidanceCalendarParams extends BaseCalendarParams {
  /** When true, only return primary guidance events */
  isPrimary?: boolean;
}

/**
 * Query parameters for Economics Calendar
 * @extends BaseCalendarParams
 */
export interface EconomicsCalendarParams extends BaseCalendarParams {
  /** Filter by country code (top-level query param, not parameters[]) */
  country?: string;
  /** Event name prefix filter; CSV supported (top-level query param) */
  eventName?: string;
}

export interface SecCalendarParams extends BaseCalendarParams {
  display?: 'flat';
  token?: string;
}

export type ConferenceCalendarParams = BaseCalendarParams;
export type OfferingsCalendarParams = BaseCalendarParams;
export type RetailCalendarParams = BaseCalendarParams;
export type SquawkCalendarParams = BaseCalendarParams;
export type OptionsActivityCalendarParams = BaseCalendarParams;

export type CalendarParamsMap = {
  conference: ConferenceCalendarParams;
  dividends: DividendsQueryParams;
  earnings: EarningsCalendarParams;
  economics: EconomicsCalendarParams;
  fda: FdaCalendarParams;
  guidance: GuidanceCalendarParams;
  ipos: IposCalendarParams;
  ma: MaCalendarParams;
  offerings: OfferingsCalendarParams;
  optionsActivity: OptionsActivityCalendarParams;
  ratings: RatingsCalendarParams;
  retail: RetailCalendarParams;
  sec: SecCalendarParams;
  splits: SplitsCalendarParams;
  squawk: SquawkCalendarParams;
};

export type ISetParams<T extends CalendarType = CalendarType> = CalendarParamsMap[T];

export type ISetParamsFDA = FdaCalendarParams;

/**
 * Normalized calendar query params (camelCase).
 * Upstream `parameters[...]` encoding happens in toUpstreamWireParams.
 */
export type CalendarDataParams = {
  action?: RatingsAction | string;
  analystId?: string;
  country?: string;
  date?: string;
  dateFrom?: string;
  dateSearchField?: string;
  dateSort?: DateSortField | string;
  dateSortStrict?: boolean;
  dateTo?: string;
  dividendYield?: number;
  dividendYieldOperation?: string;
  eventName?: string;
  fields?: string;
  firm?: string;
  firmId?: string;
  id?: string;
  importance?: Importance | number;
  ipoType?: IPOType | string;
  isPrimary?: boolean;
  page?: number;
  pageSize?: number;
  securities?: string;
  symbols?: string;
  token?: string;
  updated?: number;
};

export interface CalendarTransient {
  events?: CalendarEvents[];
  intervalCallback?: VoidFunction | null;
  isLoading: boolean;
  lastUpdatedEventsIds: CalendarEvents['id'][];
  updateInterval?: ReturnType<typeof setTimeout>;
}

export interface Saga {
  batchLoaded?: boolean;
  id: UUID;
}

// restful entities

export enum FetchMethod {
  DELETE = 'DELETE',
  GET = 'GET',
  PATCH = 'PATCH',
  POST = 'POST',
  PUT = 'PUT',
}

export enum ErrorType {
  GET_DATA = 'get_data_error',
  GET_EVENTS = 'get_events_error',
  UPDATE_EVENTS = 'update_events_error',
}

export enum CalendarCallType {
  ERROR = 'error',
  GET_CALENDAR_DATA = 'get_calendar_data',
  SET_EVENTS = 'set_calendar_events',
  SET_PARAMETERS = 'set_calendar_parameters',
  UPDATE_EVENTS = 'update_calendar_events',
}

interface CalendarEvent {
  events?: CalendarEvents[];
  feedId: CalendarFeedId;
  lastUpdatedEventsIds?: CalendarEvents['id'][];
  sagaId?: UUID;
  analysts?: any;
}

export interface ErrorEvent extends CalendarEvent {
  calendarType?: CalendarType;
  error?: SafeError;
  errorType: ErrorType;
  parameters?: CalendarDataParameters;
  type: CalendarCallType.ERROR;
}

export interface SetCalendarEvent extends CalendarEvent {
  eventsAction: EventsAction;
  newEvents: CalendarEvents[];
  parameters: CalendarDataParameters;
  requestParameters: CalendarDataParameters;
  type: CalendarCallType.SET_EVENTS;
}

export interface UpdateCalendarEvent extends CalendarEvent {
  newEvents: CalendarEvents[];
  parameters: CalendarDataParameters;
  type: CalendarCallType.UPDATE_EVENTS;
}

export interface SetParametersEvent extends CalendarEvent {
  parameters: CalendarDataParameters;
  type: CalendarCallType.SET_PARAMETERS;
}

export interface GetCalendarDataEvent {
  result: AnalystData | null;
  type: 'get_analyst_data';
}

export interface GetCompanyEventsDataEvent {
  result: CompanyEventDetails[] | null;
  type: 'get_company_events_data';
}

export interface GetSecCalendarDataEvent {
  result: SEC[] | null;
  type: 'get_sec_calendar_data';
}

export interface GetAnalystDataEvent {
  calendarType: CalendarType;
  feedId: CalendarFeedId;
  result: CalendarEvents[];
  type: CalendarCallType.GET_CALENDAR_DATA;
}

export type CalendarManagerEvent =
  | SetCalendarEvent
  | UpdateCalendarEvent
  | SetParametersEvent
  | ErrorEvent
  | GetCalendarDataEvent
  | GetCompanyEventsDataEvent
  | GetAnalystDataEvent
  | GetSecCalendarDataEvent;

export enum ExtremeDateValue {
  max = '9999-12-31',
  min = '0000-01-01',
}

export enum CalendarUpdateInterval {
  basic = 30000,
  realtime = 5000,
}

export const CalendarUpdateModeTable = {
  conference: CalendarUpdateInterval.basic,
  dividends: CalendarUpdateInterval.basic,
  earnings: CalendarUpdateInterval.basic,
  economics: CalendarUpdateInterval.basic,
  fda: CalendarUpdateInterval.basic,
  guidance: CalendarUpdateInterval.basic,
  ipos: CalendarUpdateInterval.basic,
  ma: CalendarUpdateInterval.basic,
  offerings: CalendarUpdateInterval.basic,
  optionsActivity: CalendarUpdateInterval.realtime,
  ratings: CalendarUpdateInterval.basic,
  sec: CalendarUpdateInterval.basic,
  splits: CalendarUpdateInterval.basic,
  squawk: CalendarUpdateInterval.basic,
};

// data definitions

export type DayOffset = number | null;
export type DefaultCalendarDate = string | null;

export interface DateRange {
  dateEnd?: DefaultCalendarDate;
  dateStart?: DefaultCalendarDate;
}

export interface DateDaysOffset {
  dateEndDaysOffset: DayOffset;
  dateStartDaysOffset: DayOffset;
}

export interface DateDefinition {
  defaultDates: {
    nothingSearched: DateDaysOffset;
    somethingSearched: DateDaysOffset;
  };
  name: string;
}

export type DatePresets =
  | 'today'
  | 'thisWeek'
  | 'trailing7Day'
  | 'weekToDate'
  | 'thisMonth'
  | 'trailing30day'
  | 'monthToDate'
  | 'lastMonth'
  | 'last3Months'
  | 'last1Year'
  | 'all'
  | 'clear';

export type DateSetting =
  | {
      type: 'Preset';
      date: DatePresets;
    }
  | {
      type: 'Range';
      date: DateRange;
    };

export interface AnalystData {
  firm_id: string;
  firm_name: string;
  id: string;
  name_full: string;
  ratings_accuracy: RatingsAccuracy;
  updated: number;
}

export interface RatingsAccuracy {
  smart_score: string;
  overall_success_rate: string;
  overall_avg_return_percentile: string;
  total_ratings_percentile: string;
  total_ratings: number;
  overall_gain_count: number;
  overall_loss_count: number;
  overall_average_return: string;
  overall_stdev: string;
  '1m_gain_count': string;
  '1m_loss_count': string;
  '1m_average_return': string;
  '1m_stdev': string;
  '3m_gain_count': string;
  '3m_loss_count': string;
  '3m_average_return': string;
  '3m_stdev': string;
  '9m_gain_count': string;
  '9m_loss_count': string;
  '9m_average_return': string;
  '9m_stdev': string;
  '1y_gain_count': string;
  '1y_loss_count': string;
  '1y_average_return': string;
  '1y_stdev': string;
  '2y_gain_count': string;
  '2y_loss_count': string;
  '2y_average_return': string;
  '2y_stdev': string;
  '3y_gain_count': string;
  '3y_loss_count': string;
  '3y_average_return': string;
  '3y_stdev': string;
  updated: number;
}

export interface CompanyEventDetails {
  date_end: string;
  date_start: string;
  event_name: string;
  event_type: string;
  id: string;
  importance: number;
  international_number: string;
  location: string;
  phone_number: string;
  securities: SecurityDetails[];
  source_link: string;
  start_time: string;
  tags: string[];
  updated: number;
  webcast_link: string;
}

interface SecurityDetails {
  country: string;
  cusip: string;
  exchange: string;
  isin: string;
  symbol: string;
}

export interface UnusualOptionsCalendarDataSet extends OptionsActivity {
  id: string;
  ticker: string;
  updated: number;
}

export interface TradeAlertsData {
  symbol: string;
  date_added: string;
  portfolio: string;
  limit_price: string;
  entry_price: string;
  exit_price: number;
  exit_date: string;
}
